import { Alert, Button, Checkbox, Collapse, Descriptions, Empty, Form, Input, List, Spin, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RTU } from '../../types/RTU';
import { ActionTargetReference, DeploymentCompatibilityTarget } from '../../types/actionTypes';
import ActionModalShell from './ActionModalShell';
import { ActionUploadProgress } from './ActionUploadProgress';
import CompactActionFilePicker from './CompactActionFilePicker';
import { useDeploymentCompatibilityPreview } from './useDeploymentCompatibilityPreview';

interface DeploymentUpdateModalProps {
  busy?: boolean;
  onCancel: () => void;
  onLaunch: (input: { file: File; dataPreserve: boolean; cfgPreserve: boolean; run: boolean; comment: string }) => void;
  open: boolean;
  targets: RTU[];
  uploadPercent?: number | null;
}

const TARGET_PAGE_SIZE = 100;

export const DeploymentUpdateModal = ({ busy = false, onCancel, onLaunch, open, targets, uploadPercent }: DeploymentUpdateModalProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [dataPreserve, setDataPreserve] = useState(true);
  const [cfgPreserve, setCfgPreserve] = useState(true);
  const [run, setRun] = useState(true);
  const [comment, setComment] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const actionTargets = useMemo<ActionTargetReference[]>(
    () => targets
      .map((target) => ({ rtuId: target.id, serialNo: String(target.serialNo ?? '').trim() }))
      .filter((target) => target.rtuId > 0 && Boolean(target.serialNo)),
    [targets],
  );
  const missingSerialTargets = targets.filter((target) => !String(target.serialNo ?? '').trim());
  const missingSerialTargetSummary = [
    ...missingSerialTargets.slice(0, 3).map((target) => `RTU ${target.id}`),
    ...(missingSerialTargets.length > 3 ? [`+${missingSerialTargets.length - 3}`] : []),
  ].join(', ');
  const { current: previewCurrent, error, preview, retry, state } = useDeploymentCompatibilityPreview(file, actionTargets);
  const compatibleTargets = preview?.targets.filter((target) => target.status === 'COMPATIBLE') ?? [];
  const incompatibleTargets = preview?.targets.filter((target) => target.status === 'INCOMPATIBLE') ?? [];
  const unverifiableTargets = preview?.targets.filter((target) => target.status === 'UNVERIFIABLE') ?? [];
  const canLaunch = Boolean(file) && previewCurrent && state === 'ready' && compatibleTargets.length > 0 && !busy && missingSerialTargets.length === 0;

  useEffect(() => {
    if (!open) {
      setFile(null);
      setComment('');
      setFileError(null);
      setDataPreserve(true);
      setCfgPreserve(true);
      setRun(true);
    }
  }, [open]);

  const clear = () => {
    if (busy) return;
    setFile(null);
    setComment('');
    setFileError(null);
    setDataPreserve(true);
    setCfgPreserve(true);
    setRun(true);
    onCancel();
  };

  const reviewItems = [
    { key: 'compatible', label: t('actionsModal.compatibility.compatibleCount', { count: compatibleTargets.length }), children: <TargetList targets={compatibleTargets} /> },
    { key: 'incompatible', label: t('actionsModal.compatibility.incompatibleCount', { count: incompatibleTargets.length }), children: <TargetList targets={incompatibleTargets} /> },
    { key: 'unverifiable', label: t('actionsModal.compatibility.unverifiableCount', { count: unverifiableTargets.length }), children: <TargetList targets={unverifiableTargets} /> },
  ].filter((item) => item.label && (item.key === 'compatible' ? compatibleTargets.length : item.key === 'incompatible' ? incompatibleTargets.length : unverifiableTargets.length));

  return (
    <ActionModalShell
      busy={busy}
      footer={<div className="dms-action-modal__footer"><Button disabled={busy} onClick={clear}>{t('actionsModal.cancel')}</Button><Button type="primary" disabled={!canLaunch} loading={busy} onClick={() => file && onLaunch({ file, dataPreserve, cfgPreserve, run, comment: comment.trim() })}>{t('actionsModal.deployment.launchForCompatible', { count: compatibleTargets.length })}</Button></div>}
      onCancel={clear}
      open={open}
      title={t('actionsModal.deployment.title')}
      width={680}
    >
      <div className="dms-action-modal__stack">
        <p className="dms-action-modal__target-summary">{t('actionsModal.targetsSelected', { count: targets.length })}</p>
        {missingSerialTargets.length ? <Alert type="error" showIcon message={t('actionsModal.missingSerial.title')} description={missingSerialTargetSummary} /> : null}
        <CompactActionFilePicker
          accept=".dpy"
          disabled={busy}
          file={file}
          hint={t('actionsModal.deployment.fileHint')}
          label={t('actionsModal.deployment.fileLabel')}
          onChange={(nextFile) => {
            setFileError(null);
            setFile(nextFile);
          }}
          onInvalid={() => setFileError(t('actionsModal.deployment.invalidFile'))}
        />
        {fileError ? <Alert type="error" showIcon role="alert" message={fileError} /> : null}
        <ActionUploadProgress busy={busy} uploadPercent={uploadPercent} />
        {file && state === 'loading' ? <Alert showIcon icon={<Spin size="small" />} type="info" message={t('actionsModal.compatibility.checking')} /> : null}
        {file && state === 'error' ? <Alert type="error" showIcon message={t('actionsModal.compatibility.failed')} description={<><span>{error}</span><br /><Button type="link" icon={<ReloadOutlined />} onClick={retry}>{t('actionsModal.retry')}</Button></>} /> : null}
        {preview ? <>
          <Descriptions size="small" bordered column={{ xs: 1, sm: 3 }}>
            <Descriptions.Item label={t('actionsModal.compatibility.compatible')}><Tag color="success">{compatibleTargets.length}</Tag></Descriptions.Item>
            <Descriptions.Item label={t('actionsModal.compatibility.incompatible')}><Tag color="error">{incompatibleTargets.length}</Tag></Descriptions.Item>
            <Descriptions.Item label={t('actionsModal.compatibility.unverifiable')}><Tag color="warning">{unverifiableTargets.length}</Tag></Descriptions.Item>
          </Descriptions>
          <Alert type={compatibleTargets.length ? 'info' : 'warning'} showIcon message={compatibleTargets.length ? t('actionsModal.compatibility.reviewed') : t('actionsModal.compatibility.noneCompatible')} />
          <Collapse items={reviewItems} className="dms-action-modal__review-list" />
        </> : null}
        <Form layout="vertical" className="dms-action-modal__options">
          <Form.Item label={t('actionsModal.deployment.options')}>
            <Checkbox checked={dataPreserve} onChange={(event) => setDataPreserve(event.target.checked)}>{t('actionsModal.deployment.preserveData')}</Checkbox><br />
            <Checkbox checked={cfgPreserve} onChange={(event) => setCfgPreserve(event.target.checked)}>{t('actionsModal.deployment.preserveConfig')}</Checkbox><br />
            <Checkbox checked={run} onChange={(event) => setRun(event.target.checked)}>{t('actionsModal.deployment.runAfterUpload')}</Checkbox>
          </Form.Item>
          <Form.Item label={t('actionsModal.comment')}>
            <Form.Item noStyle><Input.TextArea aria-label={t('actionsModal.comment')} autoSize={{ minRows: 3, maxRows: 6 }} maxLength={250} showCount value={comment} onChange={(event) => setComment(event.target.value)} /></Form.Item>
          </Form.Item>
        </Form>
      </div>
    </ActionModalShell>
  );
};

const TargetList = ({ targets }: { targets: DeploymentCompatibilityTarget[] }) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(TARGET_PAGE_SIZE);
  const visibleTargets = targets.slice(0, visibleCount);
  const status = (target: DeploymentCompatibilityTarget) => {
    const label = target.status === 'COMPATIBLE'
      ? t('actionsModal.compatibility.compatible')
      : target.status === 'INCOMPATIBLE'
        ? t('actionsModal.compatibility.incompatible')
        : t('actionsModal.compatibility.unverifiable');
    return <Tag aria-label={label} color={target.status === 'COMPATIBLE' ? 'success' : target.status === 'INCOMPATIBLE' ? 'error' : 'warning'}>{label}</Tag>;
  };
  if (!targets.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return <><List size="small" dataSource={visibleTargets} renderItem={(target) => {
    const title = target.rtuName ? `${target.rtuName} (${target.rtuId ?? '-'})` : `RTU ${target.rtuId ?? '-'}`;
    return <List.Item><List.Item.Meta title={title} description={<><span>{target.message || target.code || '-'}</span>{target.serialNo ? <span className="dms-action-modal__technical-detail">Serial: {target.serialNo}</span> : null}</>} />{status(target)}</List.Item>;
  }} />{targets.length > visibleTargets.length ? <Button type="link" onClick={() => setVisibleCount((current) => Math.min(current + TARGET_PAGE_SIZE, targets.length))}>{t('actionsModal.compatibility.loadMore', { count: Math.min(TARGET_PAGE_SIZE, targets.length - visibleTargets.length) })}</Button> : null}</>;
};

export default DeploymentUpdateModal;
